/**
 * Posts Requiring Attention Page
 *
 * Displays posts flagged by the BKA analysis engine as needing review,
 * updates, rewrites, retirement, or other actions.
 *
 * July 2026: surfaces the attention_type_key legend via:
 *   • Click-to-open info popover on every attention-type badge
 *   • Collapsible legend panel above the cards grid
 *
 * July 2026 (workflow): durable Done/Skip/Restore work-queue on top of the PNA
 * list, with totals + Status/Priority/Type filters. See
 * Specifications/posts-requiring-attention-workflow.md
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import {
  Loader2,
  Clock,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Info,
  BookOpen,
  Check,
  X,
  RotateCcw,
} from 'lucide-react';
import { useSite } from '../../hooks/useSite';
import { analysisApi } from '../../lib/dashboard-api';
import type {
  ContentAnalysisData,
  PostNeedingAttention,
  PostConnectivityScore,
  PostPriority,
  AttentionTypeKey,
  AttentionTypeInfo,
  AttentionWorkflowStatus,
  AttentionWorkflowMap,
} from '../../lib/dashboard-api';

// ─── Static fallback key ─────────────────────────────────────────────────────
// Used when the API response pre-dates the July 2026 BKA update and doesn't
// include an attention_type_key.  Values match the spec exactly.

const FALLBACK_KEY: AttentionTypeKey = {
  update: {
    label: 'Needs Update',
    description:
      'The post contains a specific, named data point, statistic, or external reference that is demonstrably outdated or needs freshness verification. The structure and core argument are sound.',
    action:
      'Make targeted edits to refresh the named data sources, statistics, or references identified in the rationale.',
  },
  rewrite: {
    label: 'Needs Rewrite',
    description:
      "The post's content structure or approach is fundamentally flawed, or the depth is insufficient relative to the category. The topic is still relevant but the execution needs significant reconstruction.",
    action:
      'Substantially restructure or rewrite the post. The core topic is worth keeping — the treatment needs to be overhauled.',
  },
  retire: {
    label: 'Consider Retiring',
    description:
      "The post is completely obsolete, redundant with better existing content, or no longer relevant to the site's topical focus. It provides no salvageable value in its current form.",
    action:
      'Remove or unpublish the post. Redirect its URL to the most relevant remaining post if it has inbound links.',
  },
  reposition: {
    label: 'Reposition to Another Category',
    description:
      'The post has genuine value but does not fit its current category or content cluster. It belongs in a different topical area where it would strengthen that cluster instead of weakening this one.',
    action:
      'Move the post to the category identified in the rationale. Update its internal links and category assignments accordingly.',
  },
  merge: {
    label: 'Merge with Another Post',
    description:
      'This post covers nearly the same ground as another post in the category. Together they create duplicate or near-duplicate content that dilutes topical authority. One should absorb the other.',
    action:
      'Consolidate this post into the primary post identified in the rationale. The weaker or shorter post is folded into the stronger one to create a single, more comprehensive resource.',
  },
  event_specific: {
    label: 'Event-Specific Content',
    description:
      'The post is tied to a specific conference, time-bound event, or dated experience that cannot be generalized. It is not suitable for ongoing internal linking as it would mislead readers about recency.',
    action:
      'No linking action required. Optionally update the post with a dated disclaimer, or archive it if the event is no longer relevant.',
  },
  dated_no_action_found: {
    label: 'Dated — Review Needed',
    description:
      'The post appears older or dated based on content era analysis, but the system could not identify a specific update, rewrite, or retirement recommendation. Human review is needed.',
    action:
      'Review the post manually to determine whether it should be updated, rewritten, retired, or left as-is with a dated disclaimer.',
  },
};

// ─── Constants ───────────────────────────────────────────────────────────────

const TIER_COLORS: Record<string, string> = {
  'Content Hub': 'text-emerald-400',
  'Well Connected': 'text-blue-400',
  'Emerging Connections': 'text-amber-400',
  Isolated: 'text-red-400',
};

const ACTION_BADGE_STYLES: Record<string, string> = {
  update: 'bg-blue-500/20 text-blue-400 border-blue-500/50',
  rewrite: 'bg-orange-500/20 text-orange-400 border-orange-500/50',
  retire: 'bg-red-500/20 text-red-400 border-red-500/50',
  merge: 'bg-purple-500/20 text-purple-400 border-purple-500/50',
  reposition: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/50',
  event_specific: 'bg-slate-500/20 text-slate-300 border-slate-500/50',
  dated_no_action_found: 'bg-zinc-500/20 text-zinc-300 border-zinc-500/50',
  duplicate: 'bg-pink-500/20 text-pink-400 border-pink-500/50',
  exclude: 'bg-gray-500/20 text-gray-400 border-gray-500/50',
  review: 'bg-slate-500/20 text-slate-400 border-slate-500/50',
};

const PRIORITY_BADGE_STYLES: Record<string, string> = {
  critical: 'bg-red-500 text-white',
  high: 'bg-orange-500 text-white',
  medium: 'bg-yellow-500 text-black',
  low: 'bg-gray-500 text-white',
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getPostTitle(post: PostNeedingAttention): string {
  return post.title || post.post_title || `Post #${post.post_id}`;
}

/**
 * Resolve the canonical attention type for a post.
 * Prefers the new `attention_type` field, falls back to legacy fields.
 */
function resolveType(post: PostNeedingAttention): string {
  if (post.attention_type) return post.attention_type;
  if (post.action_type && post.action_type !== 'review') return post.action_type;
  if (post.potential_action) return post.potential_action;
  if (post.exclude_reason) {
    const r = post.exclude_reason.toLowerCase();
    if (r.includes('duplicate')) return 'duplicate';
    if (r.includes('outdated')) return 'update';
    if (r.includes('thin') || r.includes('low_quality')) return 'rewrite';
    if (r.includes('retire') || r.includes('obsolete')) return 'retire';
    if (r.includes('reposition') || r.includes('category')) return 'reposition';
    if (r.includes('merge')) return 'merge';
  }
  if (post.recommended_update) return 'update';
  if (post.rewrite_rationale) return 'rewrite';
  if (post.retire_rationale) return 'retire';
  return 'review';
}

/**
 * Resolve the display priority for a post.
 * Prefers the new `priority` field, falls back to legacy fields.
 */
function resolvePriority(post: PostNeedingAttention): PostPriority | undefined {
  if (post.priority) return post.priority as PostPriority;
  return post.update_priority || post.rewrite_priority;
}

/**
 * Resolve the primary rationale text for a post.
 * Prefers the new `rationale` field, falls back to legacy reasoning fields.
 */
function resolveRationale(post: PostNeedingAttention): string | undefined {
  return (
    post.rationale ||
    post.detailed_reasoning ||
    post.action_rationale ||
    post.recommended_update ||
    post.rewrite_rationale ||
    post.retire_rationale
  );
}

function buildHubSpotEditUrl(postId: number, portalId?: string): string | null {
  if (!portalId) return null;
  return `https://app.hubspot.com/blog/${portalId}/editor/${postId}`;
}

/**
 * Recommendation identity key — matches the (post_id, action_type) key used by
 * the backend's post_attention_workflow table. Must stay in sync with
 * resolveAttentionActionType() in backend/src/handlers/analysis.js.
 */
function workflowKey(post: PostNeedingAttention): string {
  return `${post.post_id}:${resolveType(post)}`;
}

const STATUS_LABELS: Record<AttentionWorkflowStatus, string> = {
  pending: 'Pending',
  done: 'Done',
  skipped: 'Skipped',
};

// ─── TypeInfoPopover ─────────────────────────────────────────────────────────

interface TypeInfoPopoverProps {
  info: AttentionTypeInfo;
  onClose: () => void;
}

function TypeInfoPopover({ info, onClose }: TypeInfoPopoverProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, [onClose]);

  return (
    <div
      ref={ref}
      className="absolute left-0 top-full mt-1 z-50 w-72 rounded-xl border border-white/10
                 bg-[#1a1a2e] shadow-2xl shadow-black/60 p-4 text-sm"
    >
      <p className="font-semibold text-white mb-2">{info.label}</p>
      <p className="text-white/70 leading-relaxed mb-3">{info.description}</p>
      <div className="border-t border-white/10 pt-3">
        <p className="text-xs font-semibold text-white/50 uppercase tracking-wide mb-1">
          What to do
        </p>
        <p className="text-white/80 leading-relaxed">{info.action}</p>
      </div>
    </div>
  );
}

// ─── ActionTypeBadge (with click-to-open popover) ────────────────────────────

interface ActionTypeBadgeProps {
  type: string;
  typeInfo?: AttentionTypeInfo;
}

function ActionTypeBadge({ type, typeInfo }: ActionTypeBadgeProps) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);

  const styleKey = type.toLowerCase().replace(/[_\s]/g, '');
  const style =
    ACTION_BADGE_STYLES[type.toLowerCase()] ||
    ACTION_BADGE_STYLES[styleKey] ||
    ACTION_BADGE_STYLES.review;

  // Use label from key if available, otherwise format the raw type string
  const displayText =
    typeInfo?.label ||
    type
      .toLowerCase()
      .replace(/_/g, ' ')
      .split(' ')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');

  return (
    <div className="relative">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold
                    rounded uppercase border transition-opacity hover:opacity-80 ${style}
                    ${typeInfo ? 'cursor-pointer' : 'cursor-default'}`}
        title={typeInfo ? 'Click to learn more' : undefined}
      >
        {displayText}
        {typeInfo && <Info className="w-3 h-3 opacity-70 flex-shrink-0" />}
      </button>

      {open && typeInfo && <TypeInfoPopover info={typeInfo} onClose={close} />}
    </div>
  );
}

// ─── PriorityBadge ───────────────────────────────────────────────────────────

function PriorityBadge({ priority }: { priority: PostPriority }) {
  const style = PRIORITY_BADGE_STYLES[priority] || PRIORITY_BADGE_STYLES.medium;
  return (
    <span className={`px-3 py-1 text-xs font-bold rounded-full ${style}`}>
      {priority.charAt(0).toUpperCase() + priority.slice(1)}
    </span>
  );
}

// ─── ConnectivityIndicator ───────────────────────────────────────────────────

interface ConnectivityIndicatorProps {
  score?: PostConnectivityScore;
  connectivityStatus?: string;
}

function ConnectivityIndicator({ score, connectivityStatus }: ConnectivityIndicatorProps) {
  const tier = score?.tier || connectivityStatus;
  if (!tier && !score) return null;
  const tierColor = TIER_COLORS[tier || ''] || 'text-gray-400';

  return (
    <div className="flex items-center gap-3 mb-3">
      {score && (
        <div className="flex items-center gap-2">
          <span className="text-white/60 text-sm">Score:</span>
          <span className="text-white font-medium">{score.connectivityScore.toFixed(1)}</span>
        </div>
      )}
      {tier && (
        <span className={`px-2 py-0.5 text-xs rounded bg-[var(--bg-tertiary)] ${tierColor}`}>
          {tier}
        </span>
      )}
    </div>
  );
}

// ─── ReasoningContent (expandable section) ───────────────────────────────────

interface ReasoningContentProps {
  post: PostNeedingAttention;
  typeInfo?: AttentionTypeInfo;
}

function ReasoningContent({ post, typeInfo }: ReasoningContentProps) {
  const rationale = resolveRationale(post);
  const hasAlternativeCategory = !!post.alternative_category;

  if (!rationale && !hasAlternativeCategory && !typeInfo?.action) {
    return (
      <div className="mt-4 pt-4 border-t border-[var(--bg-tertiary)]">
        <p className="text-white/50 text-sm italic">No additional details available for this post.</p>
      </div>
    );
  }

  return (
    <div className="mt-4 pt-4 border-t border-[var(--bg-tertiary)] space-y-3">
      {/* AI rationale — why this post needs attention */}
      {rationale && (
        <div className="bg-blue-500/10 border-l-4 border-blue-500 rounded-r-lg p-3">
          <p className="text-white/80 text-sm leading-relaxed">{rationale}</p>
        </div>
      )}

      {/* Recommended action — what to do (from attention_type_key) */}
      {typeInfo?.action && (
        <div className="bg-amber-500/10 border-l-4 border-amber-500 rounded-r-lg p-3">
          <p className="text-xs font-semibold text-amber-400/80 uppercase tracking-wide mb-1">
            Recommended Action
          </p>
          <p className="text-white/80 text-sm leading-relaxed">{typeInfo.action}</p>
        </div>
      )}

      {/* Alternative category (reposition type) */}
      {hasAlternativeCategory && (
        <div className="bg-purple-500/10 border-l-4 border-purple-500 rounded-r-lg p-3">
          <p className="text-white/80 text-sm">
            <strong className="text-white">Alternative Category: </strong>
            {post.alternative_category}
          </p>
        </div>
      )}
    </div>
  );
}

// ─── WorkflowActions (Done / Skip / Restore) ────────────────────────────────

interface WorkflowActionsProps {
  status: AttentionWorkflowStatus;
  busy: boolean;
  onDone: () => void;
  onSkip: () => void;
  onRestore: () => void;
}

function WorkflowActions({ status, busy, onDone, onSkip, onRestore }: WorkflowActionsProps) {
  if (status === 'pending') {
    return (
      <div className="flex gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={(e) => {
            e.stopPropagation();
            onDone();
          }}
          className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-semibold
                     rounded-lg bg-emerald-500/15 text-emerald-400 border border-emerald-500/40
                     hover:bg-emerald-500/25 transition-colors disabled:opacity-50 disabled:cursor-wait"
        >
          <Check className="w-3.5 h-3.5" /> Done
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={(e) => {
            e.stopPropagation();
            onSkip();
          }}
          className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-semibold
                     rounded-lg bg-white/5 text-white/60 border border-white/15
                     hover:bg-white/10 hover:text-white/80 transition-colors disabled:opacity-50 disabled:cursor-wait"
        >
          <X className="w-3.5 h-3.5" /> Skip
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      disabled={busy}
      onClick={(e) => {
        e.stopPropagation();
        onRestore();
      }}
      className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-semibold
                 rounded-lg bg-white/5 text-white/60 border border-white/15
                 hover:bg-white/10 hover:text-white/80 transition-colors disabled:opacity-50 disabled:cursor-wait"
    >
      <RotateCcw className="w-3.5 h-3.5" /> Restore to Pending
    </button>
  );
}

function WorkflowStatusBadge({ status }: { status: AttentionWorkflowStatus }) {
  if (status === 'pending') return null;
  const style =
    status === 'done'
      ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50'
      : 'bg-white/10 text-white/60 border-white/20';
  return (
    <span className={`px-2 py-1 text-xs font-semibold rounded uppercase border ${style}`}>
      {STATUS_LABELS[status]}
    </span>
  );
}

// ─── PostCard ─────────────────────────────────────────────────────────────────

interface PostCardProps {
  post: PostNeedingAttention;
  connectivityScore?: PostConnectivityScore;
  typeInfo?: AttentionTypeInfo;
  expanded: boolean;
  onToggleExpand: () => void;
  portalId?: string;
  status: AttentionWorkflowStatus;
  actionBusy: boolean;
  onSetStatus: (status: AttentionWorkflowStatus) => void;
}

function PostCard({
  post,
  connectivityScore,
  typeInfo,
  expanded,
  onToggleExpand,
  portalId,
  status,
  actionBusy,
  onSetStatus,
}: PostCardProps) {
  const title = getPostTitle(post);
  const type = resolveType(post);
  const priority = resolvePriority(post);
  const editUrl = post.edit_url || buildHubSpotEditUrl(post.post_id, portalId);

  return (
    <div
      className={`bg-[var(--bg-secondary)] rounded-xl border-l-4 p-4 hover:shadow-lg transition-all ${
        status === 'pending' ? 'border-amber-500' : status === 'done' ? 'border-emerald-500' : 'border-white/20'
      } ${status !== 'pending' ? 'opacity-70' : ''}`}
    >
      {/* Header row */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <ActionTypeBadge type={type} typeInfo={typeInfo} />
          <WorkflowStatusBadge status={status} />
        </div>
        {priority && <PriorityBadge priority={priority} />}
      </div>

      {/* Post title */}
      <div className="mb-2">
        {editUrl ? (
          <a
            href={editUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-white font-medium hover:text-[#ffdc18] transition-colors inline-flex items-center gap-1"
            onClick={(e) => e.stopPropagation()}
          >
            {title}
            <ExternalLink className="w-3 h-3 text-white/40" />
          </a>
        ) : (
          <span className="text-white font-medium">{title}</span>
        )}
      </div>

      {/* Post ID */}
      <div className="text-white/50 text-sm mb-2">Post ID: {post.post_id}</div>

      {/* Connectivity */}
      <ConnectivityIndicator
        score={connectivityScore}
        connectivityStatus={post.connectivity_status}
      />

      {/* Categories (legacy) */}
      {post.categories && post.categories.length > 0 && (
        <div className="text-white/60 text-sm mb-3">{post.categories.join(', ')}</div>
      )}
      {post.original_category && (
        <div className="text-white/50 text-xs mb-3">From: {post.original_category}</div>
      )}

      {/* Expand toggle */}
      <button
        onClick={onToggleExpand}
        className="flex items-center gap-2 text-sm text-[#ffdc18] hover:text-[#ffdc18]/80 transition-colors w-full mb-3"
      >
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        {expanded ? 'Hide Review Details' : 'Show Review Details'}
      </button>

      {expanded && <ReasoningContent post={post} typeInfo={typeInfo} />}

      {/* Workflow actions — Done / Skip / Restore */}
      <div className="mt-3 pt-3 border-t border-[var(--bg-tertiary)]">
        <WorkflowActions
          status={status}
          busy={actionBusy}
          onDone={() => onSetStatus('done')}
          onSkip={() => onSetStatus('skipped')}
          onRestore={() => onSetStatus('pending')}
        />
      </div>
    </div>
  );
}

// ─── TotalsBar ───────────────────────────────────────────────────────────────

interface AttentionTotals {
  pending: number;
  done: number;
  skipped: number;
  total: number;
}

type StatusFilterValue = AttentionWorkflowStatus | 'all';

interface TotalsBarProps {
  totals: AttentionTotals;
  statusFilter: StatusFilterValue;
  onSelectStatus: (status: StatusFilterValue) => void;
}

function TotalsBar({ totals, statusFilter, onSelectStatus }: TotalsBarProps) {
  const cards: { key: StatusFilterValue; label: string; value: number; accent: string }[] = [
    { key: 'pending', label: 'Pending', value: totals.pending, accent: 'text-amber-400' },
    { key: 'done', label: 'Done', value: totals.done, accent: 'text-emerald-400' },
    { key: 'skipped', label: 'Skipped', value: totals.skipped, accent: 'text-white/60' },
    { key: 'all', label: 'Total', value: totals.total, accent: 'text-white' },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
      {cards.map((card) => {
        const active = statusFilter === card.key;
        return (
          <button
            key={card.key}
            type="button"
            onClick={() => onSelectStatus(card.key)}
            className={`bg-[var(--bg-secondary)] rounded-xl p-4 text-left border transition-colors ${
              active ? 'border-[#ffdc18]' : 'border-white/10 hover:border-white/25'
            }`}
          >
            <p className={`text-2xl font-bold ${card.accent}`}>{card.value}</p>
            <p className="text-sm text-white/70 uppercase tracking-wide">{card.label}</p>
          </button>
        );
      })}
    </div>
  );
}

// ─── FilterBar ───────────────────────────────────────────────────────────────

interface FilterBarProps {
  statusFilter: StatusFilterValue;
  onStatusChange: (v: StatusFilterValue) => void;
  priorityFilter: string;
  onPriorityChange: (v: string) => void;
  typeFilter: string;
  onTypeChange: (v: string) => void;
  typeOptions: { value: string; label: string }[];
}

const SELECT_CLASSES =
  'bg-[var(--bg-tertiary)] text-white text-sm rounded-lg px-3 py-2 border border-white/10 ' +
  'focus:outline-none focus:border-[#ffdc18]/60';

function FilterBar({
  statusFilter,
  onStatusChange,
  priorityFilter,
  onPriorityChange,
  typeFilter,
  onTypeChange,
  typeOptions,
}: FilterBarProps) {
  return (
    <div className="flex flex-wrap items-center gap-4 mb-4 bg-[var(--bg-secondary)] rounded-xl p-4">
      <div className="flex items-center gap-2">
        <label className="text-sm text-white/60 font-medium">Status:</label>
        <select
          value={statusFilter}
          onChange={(e) => onStatusChange(e.target.value as StatusFilterValue)}
          className={SELECT_CLASSES}
        >
          <option value="pending">Pending</option>
          <option value="done">Done</option>
          <option value="skipped">Skipped</option>
          <option value="all">All</option>
        </select>
      </div>

      <div className="flex items-center gap-2">
        <label className="text-sm text-white/60 font-medium">Priority:</label>
        <select value={priorityFilter} onChange={(e) => onPriorityChange(e.target.value)} className={SELECT_CLASSES}>
          <option value="all">All</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
          <option value="none">None / Unset</option>
        </select>
      </div>

      <div className="flex items-center gap-2">
        <label className="text-sm text-white/60 font-medium">Type:</label>
        <select value={typeFilter} onChange={(e) => onTypeChange(e.target.value)} className={SELECT_CLASSES}>
          <option value="all">All</option>
          {typeOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

// ─── LegendPanel ─────────────────────────────────────────────────────────────

interface LegendPanelProps {
  posts: PostNeedingAttention[];
  effectiveKey: AttentionTypeKey;
}

function LegendPanel({ posts, effectiveKey }: LegendPanelProps) {
  const [open, setOpen] = useState(false);

  // Only show types that are actually present in the list
  const typesInUse = new Set(posts.map(resolveType));
  const legend = Object.entries(effectiveKey).filter(([type]) => typesInUse.has(type));

  if (legend.length === 0) return null;

  return (
    <div className="bg-[var(--bg-secondary)] rounded-xl border border-white/10 overflow-hidden mb-6">
      {/* Toggle header */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 text-sm
                   text-white/70 hover:text-white transition-colors"
      >
        <span className="flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-[#ffdc18]" />
          <span className="font-medium">What do these labels mean?</span>
          <span className="text-white/40 text-xs">({legend.length} type{legend.length !== 1 ? 's' : ''} in this list)</span>
        </span>
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {/* Expanded legend */}
      {open && (
        <div className="border-t border-white/10 divide-y divide-white/5">
          {legend.map(([type, info]) => {
            const styleKey = type.toLowerCase().replace(/[_\s]/g, '');
            const style =
              ACTION_BADGE_STYLES[type.toLowerCase()] ||
              ACTION_BADGE_STYLES[styleKey] ||
              ACTION_BADGE_STYLES.review;

            return (
              <div key={type} className="px-4 py-4 space-y-2">
                {/* Badge */}
                <span
                  className={`inline-block px-2 py-1 text-xs font-semibold rounded
                              uppercase border whitespace-nowrap ${style}`}
                >
                  {info.label}
                </span>
                {/* Description + action */}
                <p className="text-white/70 text-sm leading-relaxed">{info.description}</p>
                <p className="text-xs text-white/50">
                  <span className="text-amber-400/80 font-semibold uppercase tracking-wide text-[10px]">
                    What to do:{' '}
                  </span>
                  {info.action}
                </p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Page sub-components ─────────────────────────────────────────────────────

function PageHeader({ postCount }: { postCount: number }) {
  return (
    <div className="text-center mb-6">
      <h1 className="text-3xl font-bold text-white mb-2">⚠️ Posts Requiring Attention</h1>
      <p className="text-white/60">
        {postCount} post{postCount !== 1 ? 's' : ''} that require review & possible action.
        Click an attention label for details, or expand any card to see the AI's rationale.
      </p>
    </div>
  );
}

function ReasoningControls({
  onShowAll,
  onHideAll,
  allExpanded,
  someExpanded,
}: {
  onShowAll: () => void;
  onHideAll: () => void;
  allExpanded: boolean;
  someExpanded: boolean;
}) {
  return (
    <div className="flex gap-3 mb-4">
      {!allExpanded && (
        <button
          onClick={onShowAll}
          className="px-4 py-2 bg-[var(--bg-tertiary)] text-white rounded-lg hover:bg-[var(--bg-tertiary)]/80 transition-colors text-sm"
        >
          Show All Reasoning
        </button>
      )}
      {someExpanded && (
        <button
          onClick={onHideAll}
          className="px-4 py-2 bg-[var(--bg-tertiary)] text-white rounded-lg hover:bg-[var(--bg-tertiary)]/80 transition-colors text-sm"
        >
          Hide All Reasoning
        </button>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-8 text-center">
      <div className="text-4xl mb-4">🎉</div>
      <h3 className="text-white font-semibold text-lg mb-2">No Posts Requiring Attention</h3>
      <p className="text-white/60 mb-4">
        Great job! All your posts are in good shape. Keep up the excellent work!
      </p>
      <Link
        to="/dashboard"
        className="inline-block px-4 py-2 bg-[var(--bg-tertiary)] text-white rounded-lg hover:bg-[var(--bg-tertiary)]/80 transition-colors"
      >
        Back to Dashboard
      </Link>
    </div>
  );
}

function FilteredEmptyState({ statusFilter, onClearFilters }: { statusFilter: StatusFilterValue; onClearFilters: () => void }) {
  const message =
    statusFilter === 'done'
      ? 'No posts marked Done yet.'
      : statusFilter === 'skipped'
        ? 'No posts marked Skipped yet.'
        : statusFilter === 'all'
          ? 'No posts match the selected filters.'
          : 'No pending posts requiring attention.';

  return (
    <div className="bg-[var(--bg-secondary)] border border-white/10 rounded-xl p-8 text-center">
      <h3 className="text-white font-semibold text-lg mb-2">{message}</h3>
      <p className="text-white/60 mb-4">Try a different Status, Priority, or Type filter.</p>
      <button
        onClick={onClearFilters}
        className="px-4 py-2 bg-[var(--bg-tertiary)] text-white rounded-lg hover:bg-[var(--bg-tertiary)]/80 transition-colors text-sm"
      >
        Reset Filters
      </button>
    </div>
  );
}

function NoAnalysisState() {
  return (
    <div className="bg-[var(--bg-secondary)] rounded-xl p-8 text-center">
      <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
      <h3 className="text-white font-semibold text-lg mb-2">No Analysis Results Available</h3>
      <p className="text-white/60 mb-4">Run a baseline analysis to see posts that require attention.</p>
      <Link
        to="/dashboard"
        className="inline-block px-4 py-2 bg-[#ffdc18] text-black font-semibold rounded-lg hover:bg-[#ffdc18]/90 transition-colors"
      >
        Go to Dashboard
      </Link>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="flex flex-col items-center justify-center py-16">
      <Loader2 className="w-8 h-8 text-[#ffdc18] animate-spin mb-4" />
      <p className="text-white/60">Loading posts requiring attention...</p>
    </div>
  );
}

function ErrorState({ error, onRetry }: { error: string; onRetry: () => void }) {
  return (
    <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-8 text-center">
      <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
      <h3 className="text-white font-semibold text-lg mb-2">Error Loading Data</h3>
      <p className="text-white/60 mb-4">{error}</p>
      <button
        onClick={onRetry}
        className="px-4 py-2 bg-[var(--bg-tertiary)] text-white rounded-lg hover:bg-[var(--bg-tertiary)]/80 transition-colors"
      >
        Try Again
      </button>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function PostsAttentionPage() {
  const { currentSite } = useSite();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ContentAnalysisData | null>(null);
  const [expandedPosts, setExpandedPosts] = useState<Set<number>>(new Set());

  // Optimistic local overrides layered on top of data.attentionWorkflow, keyed
  // by workflowKey(post). Lets Done/Skip/Restore update the UI immediately
  // without waiting for a full refetch.
  const [workflowOverrides, setWorkflowOverrides] = useState<Record<string, AttentionWorkflowStatus>>({});
  const [actionBusyKeys, setActionBusyKeys] = useState<Record<string, boolean>>({});
  const [actionError, setActionError] = useState<string | null>(null);

  const analysisId = searchParams.get('analysis_id');

  // Filters — synced to URL query params; default Status = Pending per spec §4.2
  const statusFilter = (searchParams.get('status') as StatusFilterValue) || 'pending';
  const priorityFilter = searchParams.get('priority') || 'all';
  const typeFilter = searchParams.get('type') || 'all';

  const updateFilter = useCallback(
    (key: 'status' | 'priority' | 'type', value: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (!value || value === 'all' || (key === 'status' && value === 'pending')) {
          next.delete(key);
        } else {
          next.set(key, value);
        }
        return next;
      });
    },
    [setSearchParams],
  );

  const clearFilters = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('status');
      next.delete('priority');
      next.delete('type');
      return next;
    });
  }, [setSearchParams]);

  const fetchData = async () => {
    if (!currentSite?.id) {
      setError('No site selected');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await analysisApi.getContentAnalysisData(
        currentSite.id,
        analysisId ? parseInt(analysisId) : undefined,
      );
      setData(response);
      setWorkflowOverrides({});
    } catch (err) {
      console.error('Failed to fetch posts requiring attention:', err);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSite?.id, analysisId]);

  const togglePost = (postId: number) => {
    setExpandedPosts((prev) => {
      const next = new Set(prev);
      next.has(postId) ? next.delete(postId) : next.add(postId);
      return next;
    });
  };

  // Preserve server sort order (server delivers by urgency: retire → reposition/merge → rewrite → update)
  const posts = (data?.postsNeedingAttention || []).filter((p) => p.post_id != null);

  const serverWorkflow: AttentionWorkflowMap = useMemo(() => data?.attentionWorkflow || {}, [data?.attentionWorkflow]);

  const getStatus = useCallback(
    (post: PostNeedingAttention): AttentionWorkflowStatus => {
      const key = workflowKey(post);
      return workflowOverrides[key] ?? serverWorkflow[key]?.status ?? 'pending';
    },
    [workflowOverrides, serverWorkflow],
  );

  // Totals reflect the FULL current analysis's attention set (before filters),
  // per spec §4.1 — computed from the live PNA list, not raw DB rows (which may
  // include Done/Skipped history for posts no longer in the current list).
  const totals: AttentionTotals = useMemo(() => {
    const t = { pending: 0, done: 0, skipped: 0, total: 0 };
    for (const post of posts) {
      t[getStatus(post)]++;
      t.total++;
    }
    return t;
  }, [posts, getStatus]);

  const typeOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const post of posts) {
      const type = resolveType(post);
      if (!seen.has(type)) {
        const label = data?.attentionTypeKey?.[type]?.label || FALLBACK_KEY[type]?.label || type;
        seen.set(type, label);
      }
    }
    return Array.from(seen.entries()).map(([value, label]) => ({ value, label }));
  }, [posts, data?.attentionTypeKey]);

  const filteredPosts = useMemo(() => {
    return posts.filter((post) => {
      if (statusFilter !== 'all' && getStatus(post) !== statusFilter) return false;
      if (priorityFilter !== 'all') {
        const priority = (resolvePriority(post) || 'none').toLowerCase();
        if (priority !== priorityFilter) return false;
      }
      if (typeFilter !== 'all' && resolveType(post) !== typeFilter) return false;
      return true;
    });
  }, [posts, statusFilter, priorityFilter, typeFilter, getStatus]);

  const showAllReasoning = () => setExpandedPosts(new Set(filteredPosts.map((p) => p.post_id)));
  const hideAllReasoning = () => setExpandedPosts(new Set());

  const allExpanded = filteredPosts.length > 0 && filteredPosts.every((p) => expandedPosts.has(p.post_id));
  const someExpanded = expandedPosts.size > 0;

  // Effective key: use API-supplied key, fall back to static constant
  const effectiveKey: AttentionTypeKey = data?.attentionTypeKey ?? FALLBACK_KEY;

  const connectivityScores = data?.connectivityScores || {};
  const portalId = currentSite?.platformId;
  const viewingOldAnalysis = analysisId && data?.analysis;

  const handleSetStatus = async (post: PostNeedingAttention, status: AttentionWorkflowStatus) => {
    if (!currentSite?.id) return;
    const key = workflowKey(post);
    if (actionBusyKeys[key]) return;

    const previousStatus = getStatus(post);
    setActionError(null);
    setActionBusyKeys((prev) => ({ ...prev, [key]: true }));
    setWorkflowOverrides((prev) => ({ ...prev, [key]: status }));

    try {
      await analysisApi.setAttentionStatus(currentSite.id, post.post_id, resolveType(post), status, {
        priority: resolvePriority(post),
        postTitle: getPostTitle(post),
        rationale: resolveRationale(post),
      });
    } catch (err) {
      console.error('Failed to update attention status:', err);
      setActionError('Could not update this recommendation. Please try again.');
      // Roll back optimistic update
      setWorkflowOverrides((prev) => ({ ...prev, [key]: previousStatus }));
    } finally {
      setActionBusyKeys((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Old analysis indicator */}
      {viewingOldAnalysis && data?.analysis && (
        <div className="flex items-center gap-2 text-white/60 bg-[var(--bg-secondary)] rounded-lg px-4 py-2">
          <Clock className="w-4 h-4" />
          <span>Viewing past analysis from {new Date(data.analysis.createdAt).toLocaleDateString()}</span>
        </div>
      )}

      {actionError && (
        <div className="flex items-center gap-2 text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-2 text-sm">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span>{actionError}</span>
        </div>
      )}

      {loading && <LoadingState />}
      {!loading && error && <ErrorState error={error} onRetry={fetchData} />}
      {!loading && !error && !data?.hasData && <NoAnalysisState />}

      {!loading && !error && data?.hasData && (
        <>
          <PageHeader postCount={posts.length} />

          {posts.length === 0 && <EmptyState />}

          {posts.length > 0 && (
            <>
              {/* Totals — clickable to set the Status filter */}
              <TotalsBar
                totals={totals}
                statusFilter={statusFilter}
                onSelectStatus={(status) => updateFilter('status', status)}
              />

              {/* Status / Priority / Type filters */}
              <FilterBar
                statusFilter={statusFilter}
                onStatusChange={(v) => updateFilter('status', v)}
                priorityFilter={priorityFilter}
                onPriorityChange={(v) => updateFilter('priority', v)}
                typeFilter={typeFilter}
                onTypeChange={(v) => updateFilter('type', v)}
                typeOptions={typeOptions}
              />

              {filteredPosts.length === 0 && (
                <FilteredEmptyState statusFilter={statusFilter} onClearFilters={clearFilters} />
              )}

              {filteredPosts.length > 0 && (
                <>
                  {/* Reasoning controls */}
                  <ReasoningControls
                    onShowAll={showAllReasoning}
                    onHideAll={hideAllReasoning}
                    allExpanded={allExpanded}
                    someExpanded={someExpanded}
                  />

                  {/* Legend panel — collapsible, above cards */}
                  <LegendPanel posts={filteredPosts} effectiveKey={effectiveKey} />

                  {/* Cards grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredPosts.map((post) => {
                      const type = resolveType(post);
                      const typeInfo: AttentionTypeInfo | undefined =
                        effectiveKey[type] ?? {
                          label: type,
                          description: '',
                          action: 'Review this post.',
                        };
                      const key = workflowKey(post);

                      return (
                        <PostCard
                          key={key}
                          post={post}
                          connectivityScore={connectivityScores[String(post.post_id)]}
                          typeInfo={typeInfo}
                          expanded={expandedPosts.has(post.post_id)}
                          onToggleExpand={() => togglePost(post.post_id)}
                          portalId={portalId}
                          status={getStatus(post)}
                          actionBusy={!!actionBusyKeys[key]}
                          onSetStatus={(status) => handleSetStatus(post, status)}
                        />
                      );
                    })}
                  </div>
                </>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
